/**
 * Derive legend symbology items from a layer-style JSON (`LayerStyle` in
 * `layerConfig.ts`) by interpreting the expression forms the styles actually
 * use, so the legend never duplicates the map symbology:
 *
 * - `match` on a feature attribute: single or grouped keys (groups are
 *   flattened to one item per key), optional fallback via `fallbackLabel`
 * - `step` bins, labelled from `stepLabels` or generated numeric ranges
 * - the `["case", ["==", <attr>, null], <colour>, <expr>]` no-data wrapper
 * - constant values (no items; the layer's single legend patch suffices)
 *
 * Anything else yields no items and the legend falls back to the plain
 * checkbox row.
 */

// Per-layer knobs, configured on `legend.symbology` in the layer table.
export interface SymbologyConfig {
	// Map a raw match key (e.g. a band number or a composite zoning code) to a
	// display label.
	formatLabel?: (key: string | number) => string;
	// Labels for `step` outputs, first-to-last; overrides the generated ranges.
	stepLabels?: string[];
	// Show the match/step fallback output as an item with this label. Fallbacks
	// are hidden when unset (usually a residual colour, not a real category).
	fallbackLabel?: string;
	// Explicit symbology items, bypassing style parsing entirely; used where the
	// style builds icon ids per feature (SafeCurves signs/plaques) so no ramp is
	// enumerable. Each item is still viewport-filtered by its own `matches`.
	items?: RichItem[];
	// Extra items appended after the derived (or explicit) ones, for a second
	// symbology dimension the colour ramp does not cover (e.g. paved vs unpaved
	// road seal); each is viewport-filtered by its own `matches`.
	extraItems?: RichItem[];
}

export interface Swatch {
	kind: 'line' | 'fill' | 'circle' | 'icon';
	color?: string;
	outline?: string;
	opacity?: number;
	width?: number;
	dash?: number[];
	// Map image id, e.g. `rlgsp:health_hospital`.
	icon?: string;
	// Pre-rendered PNG swatch URL for symbol layers (SafeCurves signs).
	image?: string;
}

export interface SymbologyItem {
	label: string;
	swatch: Swatch;
	// A secondary-dimension item (e.g. paved/unpaved) the legend renders as a
	// compact hint under the section heading rather than as a full row.
	hint?: boolean;
}

export interface RichItem extends SymbologyItem {
	matches: (props: Record<string, unknown>) => boolean;
}

/** Paved (solid) / unpaved (dashed) hints for layers that dash by `road_seal`. */
export const roadSealHints = (dash: number[]): RichItem[] => [
	{
		label: 'Paved',
		swatch: { kind: 'line', color: '#767676' },
		matches: (p) => Number(p.road_seal) === 1,
		hint: true
	},
	{
		label: 'Unpaved',
		swatch: { kind: 'line', color: '#767676', dash },
		matches: (p) => Number(p.road_seal) !== 1,
		hint: true
	}
];

// Structural view of a style JSON; all this parser reads.
interface StyleLike {
	type: string;
	layout?: unknown;
	paint?: unknown;
}

// null = "no specific key": step bins, no-data branches and match fallbacks.
type MatchKey = string | number | null;

interface Category {
	key: MatchKey;
	label: string;
	// Driver output: a colour, or an icon id for symbol layers.
	value: string;
	// True when a feature with these properties renders as this category, letting
	// the active-layers legend keep only the classes currently on the map.
	matches: (props: Record<string, unknown>) => boolean;
}

const asRecord = (v: unknown): Record<string, unknown> => (v ?? {}) as Record<string, unknown>;

/**
 * Statically resolve a paint/layout prop for the feature-value `key`:
 * constants pass through, `match` picks the branch containing the key
 * (fallback otherwise), `literal` unwraps. Other expression forms are not
 * resolvable per-key, so return undefined and the detail is omitted.
 */
function evalForKey(expr: unknown, key: MatchKey): unknown {
	if (!Array.isArray(expr)) return expr;
	const [op] = expr;
	if (op === 'literal') return expr[1];
	if (op === 'match') {
		for (let i = 2; i < expr.length - 1; i += 2) {
			const k = expr[i] as MatchKey | MatchKey[];
			if (Array.isArray(k) ? k.includes(key as string | number) : k === key) {
				return evalForKey(expr[i + 1], key);
			}
		}
		return evalForKey(expr[expr.length - 1], key);
	}
	return undefined;
}

/**
 * Evaluate the small subset of input expressions the styles key on
 * (`get` / `to-string` / `downcase` / `upcase` / `concat` / `literal`) against a
 * feature's properties, so a rendered feature can be classified back to its
 * symbology category. Unhandled forms yield `undefined`, so the feature then
 * matches no category.
 */
function evalInput(expr: unknown, props: Record<string, unknown>): unknown {
	if (!Array.isArray(expr)) return expr;
	const [op, ...args] = expr;
	const s = (a: unknown) => {
		const v = evalInput(a, props);
		return v == null ? '' : String(v);
	};
	if (op === 'get') return props[args[0] as string];
	if (op === 'literal') return args[0];
	if (op === 'to-string') return s(args[0]);
	// Styles use these to make an otherwise case-sensitive `match` tolerant of attribute casing.
	if (op === 'downcase') return s(args[0]).toLowerCase();
	if (op === 'upcase') return s(args[0]).toUpperCase();
	if (op === 'concat') return args.map(s).join('');
	return undefined;
}

// Tolerant of number/string drift between style match keys and feature
// attribute values (e.g. a risk band 1 vs the string "1").
function looseEq(a: unknown, b: MatchKey): boolean {
	if (a == null || b == null) return a === b;
	return String(a) === String(b);
}

// Round a generated band-range number to a whole number with thousands separators
// (e.g. 841.8000000000001 becomes "842"); en-US to match the US-English UI.
const bandNumberFormat = new Intl.NumberFormat('en-US', {
	maximumFractionDigits: 0
});
export const formatBandNumber = (value: number): string => bandNumberFormat.format(value);

/** Split the driver expression into one category per match key / step bin. */
function parseCategories(expr: unknown, cfg: SymbologyConfig): Category[] {
	if (!Array.isArray(expr)) return [];
	const [op] = expr;
	const format = cfg.formatLabel ?? ((k: string | number) => String(k));

	// No-data wrapper: ["case", ["==", <attr>, null], <colour>, <expr>].
	if (op === 'case' && expr.length === 4) {
		const [, test, noData, rest] = expr;
		if (Array.isArray(test) && test[0] === '==' && test[2] === null && typeof noData === 'string') {
			const attr = test[1];
			return [
				...parseCategories(rest, cfg),
				{
					key: null,
					label: 'No data',
					value: noData,
					matches: (props) => evalInput(attr, props) == null
				}
			];
		}
		return [];
	}

	if (op === 'match') {
		const input = expr[1];
		const explicitKeys: (string | number)[] = [];
		const categories: Category[] = [];
		for (let i = 2; i < expr.length - 1; i += 2) {
			const keys = (Array.isArray(expr[i]) ? expr[i] : [expr[i]]) as (string | number)[];
			const value = expr[i + 1];
			if (typeof value !== 'string') continue;
			for (const key of keys) {
				explicitKeys.push(key);
				categories.push({
					key,
					label: format(key),
					value,
					matches: (props) => looseEq(evalInput(input, props), key)
				});
			}
		}
		const fallback = expr[expr.length - 1];
		if (cfg.fallbackLabel && typeof fallback === 'string') {
			categories.push({
				key: null,
				label: cfg.fallbackLabel,
				value: fallback,
				// The fallback branch: a value present but matching no explicit key.
				matches: (props) => {
					const v = evalInput(input, props);
					return v != null && !explicitKeys.some((k) => looseEq(v, k));
				}
			});
		}
		return categories;
	}

	if (op === 'step') {
		const input = expr[1];
		// ["step", input, out0, stop1, out1, …]; out_n applies to [stop_n, stop_n+1).
		const outputs: string[] = [];
		const stops: number[] = [];
		for (let i = 2; i < expr.length; i++) {
			if (i % 2 === 0) outputs.push(expr[i] as string);
			else stops.push(expr[i] as number);
		}
		return outputs.map((value, n) => ({
			key: null,
			value,
			label:
				cfg.stepLabels?.[n] ??
				(n === 0
					? `< ${formatBandNumber(stops[0])}`
					: n === outputs.length - 1
						? `≥ ${formatBandNumber(stops[n - 1])}`
						: `${formatBandNumber(stops[n - 1])}–${formatBandNumber(stops[n])}`),
			matches: (props: Record<string, unknown>) => {
				const v = Number(evalInput(input, props));
				if (Number.isNaN(v)) return false;
				const lo = n === 0 ? -Infinity : stops[n - 1];
				const hi = n === outputs.length - 1 ? Infinity : stops[n];
				return v >= lo && v < hi;
			}
		}));
	}

	return [];
}

const num = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

// MapLibre reads a dasharray as alternating dash/gap lengths, so `[1]` or
// `[1, 0]` is solid; drop those rather than pass them to SVG, which duplicates
// an odd-length `stroke-dasharray` and would draw a dashed sample.
const dashArr = (v: unknown): number[] | undefined => {
	if (!Array.isArray(v) || !v.every((x) => typeof x === 'number')) return undefined;
	const arr = v as number[];
	return arr.some((n, i) => i % 2 === 1 && n > 0) ? arr : undefined;
};

// The constant-literal value of a dasharray (a bare numeric array or a `literal`
// wrapper), or undefined for a data-driven form.
const constDashArray = (v: unknown): unknown => {
	if (Array.isArray(v) && v.every((x) => typeof x === 'number')) return v;
	if (Array.isArray(v) && v[0] === 'literal') return v[1];
	return undefined;
};

// The keyed input of a match/step expression (the attribute it switches on), or
// undefined for a constant or unkeyed form.
function exprInput(expr: unknown): unknown {
	if (!Array.isArray(expr)) return undefined;
	const op = expr[0];
	return op === 'match' || op === 'step' ? expr[1] : undefined;
}

// A line's dash for one colour category: a constant literal applies to every
// category; a data-driven dash applies per key ONLY when it switches on the same
// attribute as the colour ramp (otherwise it is a separate dimension and stays solid).
function lineDash(paint: Record<string, unknown>, key: MatchKey): number[] | undefined {
	const constant = constDashArray(paint['line-dasharray']);
	if (constant !== undefined) return dashArr(constant);
	const colourInput = exprInput(paint['line-color']);
	const dashInput = exprInput(paint['line-dasharray']);
	if (colourInput !== undefined && JSON.stringify(colourInput) === JSON.stringify(dashInput)) {
		return dashArr(evalForKey(paint['line-dasharray'], key));
	}
	return undefined;
}

function buildSwatch(type: string, paint: Record<string, unknown>, c: Category): Swatch {
	if (type === 'line') {
		return {
			kind: 'line',
			color: c.value,
			width: num(evalForKey(paint['line-width'], c.key)),
			dash: lineDash(paint, c.key),
			opacity: num(evalForKey(paint['line-opacity'], c.key))
		};
	}
	if (type === 'fill') {
		return {
			kind: 'fill',
			color: c.value,
			outline: str(evalForKey(paint['fill-outline-color'], c.key)),
			opacity: num(evalForKey(paint['fill-opacity'], c.key))
		};
	}
	if (type === 'circle') {
		return {
			kind: 'circle',
			color: c.value,
			outline: str(evalForKey(paint['circle-stroke-color'], c.key))
		};
	}
	return { kind: 'fill', color: c.value };
}

// Symbology items paired with the predicate that tells whether a given feature
// renders as that category: the shared basis for the full legend
// (`deriveSymbology`) and the active-only legend (`deriveActiveSymbology`).
function deriveRich(style: StyleLike, cfg: SymbologyConfig): RichItem[] {
	// Extra items are the secondary dimension (e.g. paved/unpaved); tag them so the
	// legend renders them as a compact hint rather than a full row.
	const extra = (cfg.extraItems ?? []).map((e) => ({ ...e, hint: true }));
	if (cfg.items) return [...cfg.items, ...extra];
	const paint = asRecord(style.paint);
	const iconImage = asRecord(style.layout)['icon-image'];
	// Text-glyph markers (a coloured square) are coloured by text-color; show a filled patch.
	const [driver, swatch]: [unknown, (c: Category) => Swatch] =
		style.type !== 'symbol'
			? [paint[`${style.type}-color`], (c) => buildSwatch(style.type, paint, c)]
			: iconImage != null
				? [iconImage, (c) => ({ kind: 'icon', icon: c.value })]
				: [paint['text-color'], (c) => ({ kind: 'fill', color: c.value })];
	return [
		...parseCategories(driver, cfg).map((c) => ({
			label: c.label,
			swatch: swatch(c),
			matches: c.matches
		})),
		...extra
	];
}

export function deriveSymbology(style: StyleLike, cfg: SymbologyConfig = {}): SymbologyItem[] {
	return deriveRich(style, cfg).map(({ label, swatch, hint }) => ({ label, swatch, hint }));
}

// Scans features once, testing only the categories not yet seen and stopping as
// soon as every category has one; a tile layer commonly yields thousands of
// features against a ramp of up to ~100 classes.
function filterActiveItems(
	items: RichItem[],
	features: Array<{ properties: Record<string, unknown> | null }>
): SymbologyItem[] {
	const active = new Set<number>();
	for (const feature of features) {
		if (active.size === items.length) break;
		const props = feature.properties ?? {};
		for (let i = 0; i < items.length; i++) {
			if (!active.has(i) && items[i].matches(props)) active.add(i);
		}
	}
	return items
		.filter((_, i) => active.has(i))
		.map(({ label, swatch, hint }) => ({ label, swatch, hint }));
}

/**
 * Like `deriveSymbology`, but keeps only the categories at least one of the
 * supplied features (from `queryRenderedFeatures`) actually renders as, so the
 * active-layers legend lists just the classes currently drawn on the map.
 */
export function deriveActiveSymbology(
	style: StyleLike,
	cfg: SymbologyConfig,
	features: Array<{ properties: Record<string, unknown> | null }>
): SymbologyItem[] {
	return filterActiveItems(deriveRich(style, cfg), features);
}

/**
 * Swatch for a feature already returned by `queryRenderedFeatures`: MapLibre has
 * resolved the data-driven expression, so `*-color` / `icon-image` are constants
 * (`Color` / `ResolvedImage`) that `String()` renders to a CSS colour or sprite id.
 */
export function resolveFeatureSwatch(style: StyleLike): Swatch | undefined {
	if (style.type === 'symbol') {
		const icon = asRecord(style.layout)['icon-image'];
		return icon != null ? { kind: 'icon', icon: String(icon) } : undefined;
	}

	const value = asRecord(style.paint)[`${style.type}-color`];
	if (value == null) return undefined;
	const color = String(value);
	if (style.type === 'line') return { kind: 'line', color };
	if (style.type === 'circle') return { kind: 'circle', color };
	if (style.type === 'fill') return { kind: 'fill', color };
	return undefined;
}

/** LegendPatch shape for a layer type. */
export const patchStyle = (layerType: string | undefined): 'round' | 'square' | 'fill' =>
	layerType === 'circle' ? 'round' : layerType === 'fill' ? 'fill' : 'square';
